(function () {
  "use strict";

  var DEFAULT_ROOT_MARGIN = "200px 0px";
  var SEARCH_DRAWER_DESKTOP_QUERY = "(min-width: 1150px)";

  /** The theme's own desktop breakpoint, evaluated per event so a resize is picked up. */
  function isSearchDrawerDesktop() {
    return !!(window.matchMedia && window.matchMedia(SEARCH_DRAWER_DESKTOP_QUERY).matches);
  }

  function fetchSectionHtml(url, priority) {
    return fetch(url, { priority: priority || "low", credentials: "same-origin" }).then(function (response) {
      if (!response.ok) throw new Error("Section fetch failed");
      return response.text();
    });
  }

  function observeAndLoad(el, loadFn) {
    if (!("IntersectionObserver" in window)) {
      loadFn();
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(el);
          loadFn();
        });
      },
      { rootMargin: DEFAULT_ROOT_MARGIN }
    );
    observer.observe(el);
  }

  /** Deferred apps / review sections (placeholder on first paint). */
  var DeferredSection = class extends HTMLElement {
    connectedCallback() {
      if (this._observing) return;
      this._observing = true;
      if (!this.getAttribute("data-section-id")) return;
      var self = this;
      observeAndLoad(this, function () {
        self.load();
      });
    }

    /**
     * Fetch the section and swap it into the page. Memoised: the IntersectionObserver path,
     * BKDeferred.loadAbove() and repeated clicks all share one request. Resolves after the
     * host has been replaced, rejects when the fetch fails (the placeholder then stays and
     * keeps aria-busy off so it can be retried by a later caller).
     */
    load(priority) {
      if (this._promise) return this._promise;
      var sectionId = this.getAttribute("data-section-id");
      if (!sectionId) return Promise.reject(new Error("deferred-section without data-section-id"));

      var url =
        window.location.pathname +
        window.location.search +
        (window.location.search ? "&" : "?") +
        "section_id=" +
        encodeURIComponent(sectionId);

      var self = this;
      this._promise = fetchSectionHtml(url, priority)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var next = doc.querySelector("#shopify-section-" + sectionId);
          var host = self.closest(".shopify-section");
          if (!next) return;
          // importNode copies markup only: <script> tags inside the section stay inert.
          var inserted = document.importNode(next, true);
          if (host) host.replaceWith(inserted);
          else self.replaceWith(inserted);
          // Page scripts bind behaviour to the new markup here (assets/custom.js: PDF popups).
          inserted.dispatchEvent(
            new CustomEvent("deferred-section:loaded", {
              bubbles: true,
              detail: { sectionId: sectionId, root: inserted },
            })
          );
        })
        .catch(function (err) {
          self.removeAttribute("aria-busy");
          self._promise = null;
          throw err;
        });
      return this._promise;
    }
  };

  /**
   * Anchor navigation over deferred sections (sections/navigation-section.liquid, #hash loads).
   * A jump computed while placeholders are still on the page lands wrong as soon as they grow,
   * so callers load everything in front of the target first and re-resolve the target afterwards
   * (its .shopify-section host is replaced as well).
   */
  function loadAbove(target, timeoutMs) {
    if (!target) return Promise.resolve("no-target");
    var hostOf = function (el) {
      return el.closest(".shopify-section");
    };
    var targetHost = hostOf(target);
    var pending = Array.prototype.filter.call(document.querySelectorAll("deferred-section"), function (el) {
      var precedes = el.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING;
      return precedes || (targetHost && hostOf(el) === targetHost);
    });
    if (!pending.length) return Promise.resolve("nothing-pending");

    var loads = pending.map(function (el) {
      return typeof el.load === "function" ? el.load("auto") : Promise.resolve();
    });
    var settled = Promise.allSettled(loads).then(function () {
      return "loaded";
    });
    var timeout = new Promise(function (resolve) {
      setTimeout(function () {
        resolve("timeout");
      }, timeoutMs || 2500);
    });
    return Promise.race([settled, timeout]);
  }

  window.BKDeferred = window.BKDeferred || {};
  window.BKDeferred.loadAbove = loadAbove;

  /**
   * Direct call with #anchor: the browser jumps to the placeholder layout, so redo the jump once
   * loaded. Blink re-scrolls to the fragment until the load event, so the correction also waits
   * for load; by then the sticky header variables (sections/header.liquid) are final as well.
   */
  var windowLoaded = new Promise(function (resolve) {
    if (document.readyState === "complete") resolve();
    else window.addEventListener("load", resolve, { once: true });
  });

  function scrollToHashAfterLoad() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      return;
    }
    var target = document.getElementById(id);
    if (!target || !target.matches(".section-anchor, [data-section-anchor]")) return;
    if (!document.querySelector("deferred-section")) return;

    Promise.all([loadAbove(target, 2500), windowLoaded]).then(function () {
      var fresh = document.getElementById(id) || target;
      var cs = getComputedStyle(document.documentElement);
      var num = function (name) {
        return parseFloat(cs.getPropertyValue(name)) || 0;
      };
      var sticky =
        num("--sticky-header-enabled") * num("--header-height") +
        num("--sticky-announcement-bar-enabled") * num("--announcement-bar-height");
      var top = fresh.getBoundingClientRect().top + window.pageYOffset - sticky - 12;
      window.scrollTo({ top: Math.max(top, 0), behavior: "auto" });
    });
  }
  /** Metafield upsell: shell stays, product tiles load when near viewport. */
  var DeferredUpsell = class extends HTMLElement {
    connectedCallback() {
      if (this._loading) return;
      this._loading = true;
      var slot = this.querySelector("[data-deferred-upsell-slot]");
      if (!slot) return;

      var handle = this.getAttribute("data-product-handle");
      if (!handle) return;

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      var url = root + "products/" + encodeURIComponent(handle) + "?section_id=deferred-metafield-upsell";

      var self = this;
      observeAndLoad(this, function () {
        fetchSectionHtml(url)
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var content =
              doc.querySelector("[data-deferred-upsell-content]") ||
              doc.querySelector(".horizontal-product-list");
            if (content) {
              slot.replaceWith(document.importNode(content, true));
            } else {
              self.remove();
            }
          })
          .catch(function () {
            self.remove();
          });
      });
    }
  };

  if (!window.customElements.get("deferred-section")) {
    window.customElements.define("deferred-section", DeferredSection);
  }
  if (!window.customElements.get("deferred-upsell")) {
    window.customElements.define("deferred-upsell", DeferredUpsell);
  }

  // Only after the define calls above: this script runs deferred, i.e. with readyState
  // "interactive", and the placeholders get their load() during the upgrade.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scrollToHashAfterLoad, { once: true });
  } else {
    scrollToHashAfterLoad();
  }

  /** Search drawer bestsellers: fetch header section HTML on first open, not on every page. */
  var DeferredSearchProducts = class extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      var self = this;
      var load = this._load.bind(this);
      var drawer = this.closest("search-drawer, .search-drawer");
      var form = this.closest("form");

      this._abortController = new AbortController();
      var options = { signal: this._abortController.signal };

      if (drawer) {
        // On desktop the drawer is not a dialog: custom.js:1736-1742 sets "open" on every page at
        // DOMContentLoaded, so "open" and the dialog:after-show that may follow it are page load,
        // not intent. There the load waits for search-drawer__active (added on input focus) or for
        // the pointerenter/focusin fallbacks below. Under the breakpoint the drawer really is a
        // dialog and both signals mean the user opened it. Checked when the event fires, not at
        // bind time, so crossing the breakpoint is picked up.
        drawer.addEventListener(
          "dialog:after-show",
          function () {
            if (isSearchDrawerDesktop()) return;
            load();
          },
          options
        );

        // The drawer can already carry "open" or the active class when we bind, so react to
        // the transition into either state rather than to the state itself.
        this._wasOpen = drawer.hasAttribute("open");
        this._wasActive = drawer.classList.contains("search-drawer__active");

        this._openObserver = new MutationObserver(function () {
          var isOpen = drawer.hasAttribute("open");
          var isActive = drawer.classList.contains("search-drawer__active");
          var openedByAttribute = isOpen && !self._wasOpen;
          var openedByClass = isActive && !self._wasActive;

          // Both states are tracked in every case, so the breakpoint check cannot desync them.
          self._wasOpen = isOpen;
          self._wasActive = isActive;

          if (openedByClass || (openedByAttribute && !isSearchDrawerDesktop())) load();
        });
        this._openObserver.observe(drawer, { attributes: true, attributeFilter: ["open", "class"] });
      }

      // Fallbacks on the search form: it stays hoverable and focusable while the results
      // panel itself is still display:none.
      var fallbackRoot = form || drawer || this;
      fallbackRoot.addEventListener("pointerenter", load, options);
      fallbackRoot.addEventListener("focusin", load, options);
    }
    disconnectedCallback() {
      // A re-rendered header (theme editor) re-runs connectedCallback on the same instance;
      // drop the old listeners and observer so it binds exactly once again.
      this._stopListening();
      this._bound = false;
    }
    _stopListening() {
      if (this._abortController) this._abortController.abort();
      if (this._openObserver) this._openObserver.disconnect();
    }
    _load() {
      if (this._loading || this._loaded) return;
      var sectionId = this.getAttribute("data-section-id");
      var slot = this.querySelector("[data-search-products-slot]");
      if (!sectionId || !slot) return;

      this._loading = true;
      var url =
        window.location.pathname +
        window.location.search +
        (window.location.search ? "&" : "?") +
        "section_id=" +
        encodeURIComponent(sectionId);
      var self = this;
      fetchSectionHtml(url, "auto")
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var list =
            doc.querySelector(".cs_custom_recommended_products_list") ||
            doc.querySelector(".cs_custom_recommended_products_lists");
          if (!list) throw new Error("Search products missing in section response");
          slot.replaceWith(document.importNode(list, true));
          self._loaded = true;
          self._stopListening();
        })
        .catch(function () {
          // Listeners stay attached, so the next open retries.
          self._loading = false;
        });
    }
  };

  if (!window.customElements.get("deferred-search-products")) {
    window.customElements.define("deferred-search-products", DeferredSearchProducts);
  }
})();
